export {};

// Job title suggestions
//
// On the 'Job requirements' step, as the user types into the title input,
// we fetch matching titles from a curated server-side list (not historic
// job_ads, to avoid leaking customer-supplied strings) and render a small
// dropdown the user can pick from with click or Enter. Picking a
// suggestion fills the title input.
//
// The companion category-autofill behaviour for ILJ-753 lives in
// category_autofill_from_title.ts so the two tickets stay separable.
//
// Suggestions are intentionally vanilla-JS rather than React because the
// surrounding step is a server-rendered Slim form.

interface Suggestion {
  title: string;
  category_id: number | null;
}

const SUGGESTIONS_URL = '/post/title-suggestions';
const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 2;

declare global {
  interface Window {
    /** Last suggestion selected by the user from the dropdown, or null. */
    ljSelectedJobTitleSuggestion: Suggestion | null;
  }
}

const initialize = () => {
  const input = document.getElementById('job-title') as HTMLInputElement | null;
  if (!input) return;

  // Avoid double-mounting if the script is imported twice (Turbolinks may
  // fire `turbolinks:load` multiple times on cached visits).
  if (input.dataset.suggestionsBound === 'true') return;
  input.dataset.suggestionsBound = 'true';

  const dropdown = document.createElement('ul');
  dropdown.className = 'lj-title-suggestions';
  dropdown.setAttribute('role', 'listbox');
  dropdown.id = 'lj-title-suggestions-list';
  dropdown.hidden = true;

  const wrapper = input.parentElement;
  if (!wrapper) return;
  // Wrapper positioning is set by the `.lj-title-suggestions-wrapper`
  // class in post_wizard.scss; no inline style needed.
  wrapper.classList.add('lj-title-suggestions-wrapper');
  wrapper.appendChild(dropdown);

  // ARIA wiring for the combobox pattern.
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-controls', dropdown.id);
  input.setAttribute('aria-expanded', 'false');

  let activeIndex = -1;
  let currentSuggestions: Suggestion[] = [];
  let debounceTimer: number | null = null;
  let abortController: AbortController | null = null;

  window.ljSelectedJobTitleSuggestion = null;

  const closeDropdown = () => {
    dropdown.hidden = true;
    dropdown.innerHTML = '';
    activeIndex = -1;
    currentSuggestions = [];
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  };

  const optionIdFor = (idx: number) => `lj-title-suggestion-${idx}`;

  const highlight = (idx: number) => {
    const items = dropdown.querySelectorAll<HTMLLIElement>('li');
    items.forEach((el, i) => {
      const isActive = i === idx;
      el.classList.toggle('is-active', isActive);
      el.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    activeIndex = idx;
    if (idx >= 0) {
      input.setAttribute('aria-activedescendant', optionIdFor(idx));
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  };

  const selectSuggestion = (suggestion: Suggestion) => {
    input.value = suggestion.title;
    window.ljSelectedJobTitleSuggestion = suggestion;
    // Notify category-autofill (and anything else listening) that a
    // suggestion was chosen, with the picked payload.
    input.dispatchEvent(
      new CustomEvent('lj:title-suggestion-selected', {
        bubbles: true,
        detail: suggestion,
      }),
    );
    closeDropdown();
    input.focus();
  };

  const render = (suggestions: Suggestion[]) => {
    currentSuggestions = suggestions;
    if (suggestions.length === 0) {
      closeDropdown();
      return;
    }

    dropdown.innerHTML = '';
    suggestions.forEach((suggestion, idx) => {
      const li = document.createElement('li');
      li.className = 'lj-title-suggestions-item';
      li.id = optionIdFor(idx);
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.textContent = suggestion.title;
      li.addEventListener('mousedown', event => {
        // mousedown beats the input's blur, which would otherwise close the
        // dropdown before the click registers.
        event.preventDefault();
        selectSuggestion(suggestion);
      });
      li.addEventListener('mouseenter', () => highlight(idx));
      dropdown.appendChild(li);
    });
    dropdown.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    activeIndex = -1;
  };

  const fetchSuggestions = (query: string) => {
    if (abortController) abortController.abort();
    abortController = new AbortController();
    const thisController = abortController;

    fetch(`${SUGGESTIONS_URL}?q=${encodeURIComponent(query)}`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: thisController.signal,
    })
      .then(response => (response.ok ? response.json() : []))
      .then((data: Suggestion[]) => {
        // Drop the response if the user blurred / tabbed away while it was
        // in flight, so a slow response can't re-open the dropdown after
        // the field lost focus.
        if (document.activeElement !== input) return;
        if (input.value.trim().length < MIN_QUERY_LENGTH) return;
        render(Array.isArray(data) ? data : []);
      })
      .catch(err => {
        // Aborts are expected when a newer keystroke supersedes this fetch.
        if (err && (err as DOMException).name === 'AbortError') return;
        // For real network / parse errors, close the dropdown so we don't
        // leave stale suggestions on screen.
        closeDropdown();
      });
  };

  input.addEventListener('input', () => {
    // The user is typing freely; any previously selected suggestion is no
    // longer the "winner" once the text diverges from it.
    if (
      window.ljSelectedJobTitleSuggestion &&
      window.ljSelectedJobTitleSuggestion.title !== input.value
    ) {
      window.ljSelectedJobTitleSuggestion = null;
    }

    const query = input.value.trim();

    if (debounceTimer) window.clearTimeout(debounceTimer);
    if (query.length < MIN_QUERY_LENGTH) {
      closeDropdown();
      return;
    }
    debounceTimer = window.setTimeout(() => fetchSuggestions(query), DEBOUNCE_MS);
  });

  input.addEventListener('keydown', (event: KeyboardEvent) => {
    if (dropdown.hidden) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      highlight(Math.min(activeIndex + 1, currentSuggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      highlight(Math.max(activeIndex - 1, 0));
    } else if (event.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < currentSuggestions.length) {
        event.preventDefault();
        selectSuggestion(currentSuggestions[activeIndex]);
      }
    } else if (event.key === 'Escape') {
      closeDropdown();
    }
  });

  input.addEventListener('blur', () => {
    // Defer so click on an item still wins.
    window.setTimeout(closeDropdown, 100);
  });
};

// Turbolinks fires `turbolinks:load` for the initial navigation and for
// subsequent visits (including cached restores). The post wizard is
// Turbolinks-driven, so we must NOT rely on `DOMContentLoaded` alone or
// the script wouldn't bind when arriving at step 1 via a Turbolinks
// visit.
document.addEventListener('turbolinks:load', initialize);

export {};
